import { useMemo, useState, useEffect } from "react";
import { useMutation, useIsMutating, useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import {
  BadgeCheck,
  Eye,
  FileText,
  Image as ImageIcon,
  RefreshCcw,
  Sparkles,
  Wallet,
  Building2,
  Smartphone,
  Save,
} from "lucide-react";

import { Button } from "./common/ui/button";
import Dropzone from "./common/Dropzone";
import { uploadKYCDoc, updateUser } from "../services/index";
import { getUser } from "../services/index";
import { useToast } from "./common/ui/use-toast";
import { CheckIcon } from "../assests/svgs/CheckIcon";
import { REACT_APP_BASE_URL } from "src/helpers/constants";

type DocumentKey = "profile_picture" | "user_id_image";

const DOCS: Array<{
  key: DocumentKey;
  title: string;
  description: string;
  note: string;
  icon: JSX.Element;
}> = [
  {
    key: "profile_picture",
    title: "Profile picture",
    description: "Upload a clear picture of the account holder.",
    note: "Use a recent photo with good lighting.",
    icon: <ImageIcon size={16} />,
  },
  {
    key: "user_id_image",
    title: "Identification document",
    description: "Upload a government-issued ID, passport, or driver’s license.",
    note: "Make sure the document details are legible.",
    icon: <FileText size={16} />,
  },
];

const STEPS = [
  {
    title: "Upload photo",
    description: "Add a profile picture that matches the account holder.",
  },
  {
    title: "Upload ID",
    description: "Attach a clear government-issued identification document.",
  },
  {
    title: "Review",
    description: "Confirm the documents and finish the verification flow.",
  },
];

const GHANA_BANKS = [
  { code: "GCB", name: "GCB Bank" },
  { code: "ECO", name: "Ecobank Ghana" },
  { code: "SCB", name: "Standard Chartered Bank" },
  { code: "ABG", name: "Absa Bank Ghana" },
  { code: "SBG", name: "Stanbic Bank Ghana" },
  { code: "FBN", name: "FBNBank Ghana" },
  { code: "ZBL", name: "Zenith Bank Ghana" },
  { code: "FDL", name: "Fidelity Bank Ghana" },
  { code: "CAL", name: "CalBank" },
  { code: "ADB", name: "Agricultural Development Bank" },
  { code: "UBA", name: "United Bank for Africa" },
  { code: "RBG", name: "Republic Bank Ghana" },
  { code: "PRB", name: "Prudential Bank" },
  { code: "GTB", name: "Guaranty Trust Bank Ghana" },
  { code: "BOA", name: "Bank of Africa Ghana" },
  { code: "SOC", name: "Societe Generale Ghana" },
  { code: "CBG", name: "Consolidated Bank Ghana" },
  { code: "ACC", name: "Access Bank Ghana" },
  { code: "NTB", name: "National Investment Bank" },
];

const KYCVerification = () => {
  const { toast } = useToast();
  const isMutating = useIsMutating() > 0;

  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [replaceProfilePicture, setReplaceProfilePicture] = useState<boolean>(false);
  const [replaceIDUpload, setReplaceIDUpload] = useState<boolean>(false);
  const [activeUpload, setActiveUpload] = useState<DocumentKey | null>(null);

  const { data: userData, refetch: refetchUserData } = useQuery({
    queryKey: ["userDetails"],
    queryFn: () => getUser(),
  });

  const userId = userData?.id;
  const isApproved = userData?.kyc_status === "approved";

  const [idNumber, setIdNumber] = useState("");

  useEffect(() => {
    if (userData?.id_number) {
      setIdNumber(userData.id_number);
    }
  }, [userData?.id_number]);

  const idNumberMutation = useMutation({
    mutationKey: ["updateIdNumber"],
    mutationFn: () => updateUser(userId, { id_number: idNumber }),
    onSuccess: async () => {
      await refetchUserData();
      toast({ description: "Ghana Card number saved successfully." });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save Ghana Card number.",
      });
    },
  });

  const profilePictureMutation = useMutation({
    mutationKey: ["profilePicture"],
    mutationFn: (formData: FormData) => uploadKYCDoc(userId, formData),
    onSuccess: async () => {
      await refetchUserData();
      setReplaceProfilePicture(false);
      setActiveUpload(null);
      setIsFinished(true);
      toast({
        description: "Profile picture uploaded successfully.",
      });
    },
    onError: () => {
      setActiveUpload(null);
      setIsFinished(true);
      setReplaceProfilePicture(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to upload profile picture.",
      });
    },
  });

  const identificationDocumentMutation = useMutation({
    mutationFn: (formData: FormData) => uploadKYCDoc(userId, formData),
    onSuccess: async () => {
      await refetchUserData();
      setReplaceIDUpload(false);
      setActiveUpload(null);
      setIsFinished(true);
      toast({
        description: "Identification document uploaded successfully.",
      });
    },
    onError: () => {
      setActiveUpload(null);
      setReplaceIDUpload(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to upload identification document.",
      });
      setIsFinished(true);
    },
  });

  const [payoutTab, setPayoutTab] = useState<"momo" | "bank">("momo");
  const [payoutFields, setPayoutFields] = useState({
    payout_momo_network: "",
    payout_momo_number: "",
    payout_momo_name: "",
    payout_bank_code: "",
    payout_bank_name: "",
    payout_bank_account_number: "",
    payout_bank_account_name: "",
    payout_preference: "momo" as "momo" | "bank",
  });

  useEffect(() => {
    if (userData) {
      setPayoutFields({
        payout_momo_network: userData.payout_momo_network || "",
        payout_momo_number: userData.payout_momo_number || "",
        payout_momo_name: userData.payout_momo_name || "",
        payout_bank_code: userData.payout_bank_code || "",
        payout_bank_name: userData.payout_bank_name || "",
        payout_bank_account_number: userData.payout_bank_account_number || "",
        payout_bank_account_name: userData.payout_bank_account_name || "",
        payout_preference: userData.payout_preference || "momo",
      });
      if (userData.payout_preference === "bank") {
        setPayoutTab("bank");
      }
    }
  }, [userData]);

  const payoutMutation = useMutation({
    mutationKey: ["updatePayoutDetails"],
    mutationFn: () =>
      updateUser(userId, {
        ...payoutFields,
        payout_preference: payoutTab,
      }),
    onSuccess: async () => {
      await refetchUserData();
      toast({ description: "Settlement details saved successfully." });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save settlement details.",
      });
    },
  });

  const handlePayoutFieldChange = (field: string, value: string) => {
    setPayoutFields((prev) => ({ ...prev, [field]: value }));
  };

  const hasPayoutDetails =
    (userData?.payout_momo_number && userData?.payout_momo_network) ||
    (userData?.payout_bank_account_number && userData?.payout_bank_code);

  const uploadedCount = useMemo(
    () =>
      [userData?.user_images?.profile_picture, userData?.user_images?.user_id_image].filter(Boolean)
        .length,
    [userData],
  );

  const isKycReady = uploadedCount === 2;
  const completionPercent = Math.round((uploadedCount / 2) * 100);
  const activeStage = isKycReady ? 3 : uploadedCount === 1 ? 2 : 1;

  const handleUpload = (docKey: DocumentKey, formData: FormData) => {
    setIsFinished(false);
    setActiveUpload(docKey);

    if (docKey === "profile_picture") {
      profilePictureMutation.mutate(formData);
      return;
    }

    identificationDocumentMutation.mutate(formData);
  };

  const openDocument = (path?: string) => {
    if (path) {
      window.open(`${REACT_APP_BASE_URL}${path}`, "_blank");
    }
  };

  return (
    <KycShell>
      <KycHeader>
        <div>
          <SectionEyebrow>DOCUMENTS</SectionEyebrow>
          <KycTitle>KYC verification</KycTitle>
          <KycDescription>
            Follow the steps below to upload your verification files and complete the review flow.
          </KycDescription>
        </div>

        <HeaderBadge $tone={isKycReady ? "emerald" : "amber"}>
          <BadgeCheck size={14} />
          {isKycReady ? "KYC ready" : `${uploadedCount} of 2 uploaded`}
        </HeaderBadge>
      </KycHeader>

      <ProgressCard>
        <ProgressRow>
          <div>
            <ProgressValue>{completionPercent}% complete</ProgressValue>
            <ProgressCopy>{isKycReady ? "You are ready for review." : "Finish the remaining upload steps."}</ProgressCopy>
          </div>
          <ProgressBadge>{uploadedCount}/2 uploaded</ProgressBadge>
        </ProgressRow>
        <ProgressTrack>
          <ProgressFill $value={completionPercent} />
        </ProgressTrack>
      </ProgressCard>

      <StepperGrid>
        {STEPS.map((step, index) => {
          const stepNumber = index + 1;
          const isActive = activeStage === stepNumber;
          const isComplete = stepNumber < activeStage;

          return (
            <StepperCard key={step.title} $active={isActive} $complete={isComplete}>
              <StepperNumber $active={isActive} $complete={isComplete}>
                {isComplete ? <BadgeCheck size={14} /> : stepNumber}
              </StepperNumber>
              <div>
                <StepperTitle>{step.title}</StepperTitle>
                <StepperDescription>{step.description}</StepperDescription>
              </div>
              {index < STEPS.length - 1 && <StepperConnector aria-hidden="true" />}
            </StepperCard>
          );
        })}
      </StepperGrid>

      {isApproved && (
        <CompletionBanner>
          <CompletionIcon>
            <CheckIcon fill="white" width={20} height={20} />
          </CompletionIcon>
          <div>
            <CompletionTitle>Your KYC has been approved.</CompletionTitle>
            <CompletionCopy>
              Your identity has been verified. Documents and ID details are locked and cannot be changed.
            </CompletionCopy>
          </div>
        </CompletionBanner>
      )}

      {!isApproved && isKycReady && (
        <CompletionBanner>
          <CompletionIcon>
            <CheckIcon fill="white" width={20} height={20} />
          </CompletionIcon>
          <div>
            <CompletionTitle>All verification files are in place.</CompletionTitle>
            <CompletionCopy>
              Your profile image and identification document are uploaded and ready for the next step.
            </CompletionCopy>
          </div>
        </CompletionBanner>
      )}

      <SettlementCard>
        <SettlementHeader>
          <div>
            <SectionEyebrow>IDENTIFICATION</SectionEyebrow>
            <SettlementTitle>Ghana Card details</SettlementTitle>
            <SettlementDescription>
              Enter your Ghana Card number for identity verification.
            </SettlementDescription>
          </div>
          <HeaderBadge $tone={idNumber ? "emerald" : "amber"}>
            <FileText size={14} />
            {idNumber ? "Provided" : "Required"}
          </HeaderBadge>
        </SettlementHeader>

        <FieldGrid>
          <FieldGroup $full>
            <FieldLabel>Ghana Card number</FieldLabel>
            <FieldInput
              type="text"
              placeholder="e.g. GHA-XXXXXXXXX-X"
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value)}
              readOnly={isApproved}
              style={isApproved ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
            />
          </FieldGroup>
        </FieldGrid>

        {!isApproved && (
          <SettlementActions>
            <Button
              type="button"
              onClick={() => idNumberMutation.mutate()}
              disabled={idNumberMutation.isPending || !idNumber.trim()}
            >
              <Save size={14} />
              {idNumberMutation.isPending ? "Saving..." : "Save Ghana Card number"}
            </Button>
          </SettlementActions>
        )}
      </SettlementCard>

      <DocumentGrid>
        {DOCS.map((doc) => {
          const uploadedPath = userData?.user_images?.[doc.key];
          const isUploaded = Boolean(uploadedPath);
          const isReplacing =
            (doc.key === "profile_picture" && replaceProfilePicture) ||
            (doc.key === "user_id_image" && replaceIDUpload);

          return (
            <DocumentCard key={doc.key}>
              <DocumentHeader>
                <DocumentIcon>{doc.icon}</DocumentIcon>
                <div>
                  <DocumentTitle>{doc.title}</DocumentTitle>
                  <DocumentDescription>{doc.description}</DocumentDescription>
                </div>
              </DocumentHeader>

              <DocumentMeta>
                <DocumentNote>{doc.note}</DocumentNote>
                <DocumentStatus $tone={isUploaded ? "emerald" : "amber"}>
                  {isUploaded ? "Uploaded" : "Missing"}
                </DocumentStatus>
              </DocumentMeta>

              {!isApproved && (!isUploaded || isReplacing) ? (
                <UploadShell>
                  <Dropzone
                    onFileUpload={(formData) => handleUpload(doc.key, formData)}
                    isLoading={isMutating && activeUpload === doc.key}
                    id={doc.key}
                    isFinished={isFinished}
                  />
                </UploadShell>
              ) : isUploaded ? (
                <UploadedState>
                  <PreviewFrame>
                    {doc.key === "profile_picture" ? (
                      <img
                        src={`${REACT_APP_BASE_URL}${uploadedPath}`}
                        alt={doc.title}
                        width={104}
                        height={104}
                        style={{ objectFit: "cover", borderRadius: 16 }}
                      />
                    ) : (
                      <FilePreview>
                        <FileText size={28} />
                        <p>ID document</p>
                      </FilePreview>
                    )}
                  </PreviewFrame>

                  <UploadedCopy>
                    <CheckMark>
                      <CheckIcon fill="white" width={16} height={16} />
                    </CheckMark>
                    <p>{`You have successfully uploaded your ${doc.title.toLowerCase()}.`}</p>
                  </UploadedCopy>

                  <ActionRow>
                    <Button type="button" variant="outline" onClick={() => openDocument(uploadedPath)}>
                      <Eye size={14} />
                      View
                    </Button>
                    {!isApproved && (
                      <Button
                        type="button"
                        onClick={() =>
                          doc.key === "profile_picture"
                            ? setReplaceProfilePicture(true)
                            : setReplaceIDUpload(true)
                        }
                      >
                        <RefreshCcw size={14} />
                        Replace upload
                      </Button>
                    )}
                  </ActionRow>
                </UploadedState>
              ) : null}
            </DocumentCard>
          );
        })}
      </DocumentGrid>

      <SettlementCard>
        <SettlementHeader>
          <div>
            <SectionEyebrow>SETTLEMENT</SectionEyebrow>
            <SettlementTitle>Payout / Settlement details</SettlementTitle>
            <SettlementDescription>
              Add your mobile money or bank account details for GHS wallet withdrawals.
            </SettlementDescription>
          </div>
          <HeaderBadge $tone={hasPayoutDetails ? "emerald" : "amber"}>
            <Wallet size={14} />
            {hasPayoutDetails ? "Details saved" : "Not configured"}
          </HeaderBadge>
        </SettlementHeader>

        <TabRow>
          <Tab $active={payoutTab === "momo"} onClick={() => setPayoutTab("momo")}>
            <Smartphone size={14} />
            Mobile Money
          </Tab>
          <Tab $active={payoutTab === "bank"} onClick={() => setPayoutTab("bank")}>
            <Building2 size={14} />
            Bank Account
          </Tab>
        </TabRow>

        {payoutTab === "momo" ? (
          <FieldGrid>
            <FieldGroup>
              <FieldLabel>Network</FieldLabel>
              <FieldSelect
                value={payoutFields.payout_momo_network}
                onChange={(e) => handlePayoutFieldChange("payout_momo_network", e.target.value)}
              >
                <option value="">Select network</option>
                <option value="MTN">MTN</option>
                <option value="VOD">Vodafone</option>
                <option value="AIR">AirtelTigo</option>
              </FieldSelect>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Mobile number</FieldLabel>
              <FieldInput
                type="tel"
                placeholder="e.g. 0241234567"
                maxLength={15}
                value={payoutFields.payout_momo_number}
                onChange={(e) => handlePayoutFieldChange("payout_momo_number", e.target.value)}
              />
            </FieldGroup>
            <FieldGroup $full>
              <FieldLabel>Account name</FieldLabel>
              <FieldInput
                type="text"
                placeholder="Name on MoMo account"
                value={payoutFields.payout_momo_name}
                onChange={(e) => handlePayoutFieldChange("payout_momo_name", e.target.value)}
              />
            </FieldGroup>
          </FieldGrid>
        ) : (
          <FieldGrid>
            <FieldGroup>
              <FieldLabel>Bank name</FieldLabel>
              <FieldSelect
                value={payoutFields.payout_bank_name}
                onChange={(e) => {
                  const selected = GHANA_BANKS.find((b) => b.name === e.target.value);
                  handlePayoutFieldChange("payout_bank_name", e.target.value);
                  handlePayoutFieldChange("payout_bank_code", selected?.code || "");
                }}
              >
                <option value="">Select bank</option>
                {GHANA_BANKS.map((bank) => (
                  <option key={bank.code} value={bank.name}>
                    {bank.name}
                  </option>
                ))}
              </FieldSelect>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Account number</FieldLabel>
              <FieldInput
                type="text"
                placeholder="Bank account number"
                value={payoutFields.payout_bank_account_number}
                onChange={(e) => handlePayoutFieldChange("payout_bank_account_number", e.target.value)}
              />
            </FieldGroup>
            <FieldGroup $full>
              <FieldLabel>Account name</FieldLabel>
              <FieldInput
                type="text"
                placeholder="Name on bank account"
                value={payoutFields.payout_bank_account_name}
                onChange={(e) => handlePayoutFieldChange("payout_bank_account_name", e.target.value)}
              />
            </FieldGroup>
          </FieldGrid>
        )}

        <SettlementActions>
          <Button
            type="button"
            onClick={() => payoutMutation.mutate()}
            disabled={payoutMutation.isPending}
          >
            <Save size={14} />
            {payoutMutation.isPending ? "Saving..." : "Save settlement details"}
          </Button>
        </SettlementActions>
      </SettlementCard>

      <ReviewCard>
        <ReviewHeader>
          <div>
            <SectionEyebrow>REVIEW</SectionEyebrow>
            <ReviewTitle>Current verification state</ReviewTitle>
            <ReviewCopy>
              {isKycReady
                ? "Your KYC pack is complete and ready for the final review state."
                : "Finish the remaining upload step(s) to unlock the review state."}
            </ReviewCopy>
          </div>
          <ReviewPill $tone={isKycReady ? "emerald" : "amber"}>
            <Sparkles size={14} />
            {isKycReady ? "Complete" : "In progress"}
          </ReviewPill>
        </ReviewHeader>

        <ReviewActions>
          <Button
            type="button"
            variant="outline"
            onClick={() => openDocument(userData?.user_images?.profile_picture)}
            disabled={!userData?.user_images?.profile_picture}
          >
            <Eye size={14} />
            Open profile picture
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => openDocument(userData?.user_images?.user_id_image)}
            disabled={!userData?.user_images?.user_id_image}
          >
            <Eye size={14} />
            Open ID document
          </Button>
        </ReviewActions>
      </ReviewCard>
    </KycShell>
  );
};

const KycShell = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const KycHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SectionEyebrow = styled.p`
  margin: 0 0 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.24em;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
`;

const KycTitle = styled.h2`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: clamp(1.5rem, 2vw, 2rem);
  line-height: 1.15;
`;

const KycDescription = styled.p`
  margin: 0.55rem 0 0;
  color: ${(props) => props.theme.colors.textSecondary};
  line-height: 1.65;
  max-width: 68ch;
`;

const HeaderBadge = styled.span<{ $tone: "emerald" | "amber" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.55rem 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  background: ${(props) => (props.$tone === "emerald" ? "rgba(16, 185, 129, 0.12)" : "rgba(245, 158, 11, 0.12)")};
  color: ${(props) => (props.$tone === "emerald" ? "#047857" : "#b45309")};
`;

const ProgressCard = styled.section`
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(255, 255, 255, 0.96);
  padding: 1rem 1.1rem;
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.04);
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;
`;

const ProgressValue = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: 1.1rem;
  font-weight: 800;
`;

const ProgressCopy = styled.p`
  margin: 0.2rem 0 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.88rem;
  line-height: 1.55;
`;

const ProgressBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.12);
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 0.6rem;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.15);
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $value: number }>`
  height: 100%;
  width: ${(props) => props.$value}%;
  border-radius: inherit;
  background: linear-gradient(90deg, #e11d48, #fb7185);
`;

const StepperGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StepperCard = styled.div<{ $active: boolean; $complete: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1rem;
  border-radius: 20px;
  border: 1px solid
    ${(props) => (props.$complete ? "rgba(16, 185, 129, 0.24)" : props.$active ? "rgba(225, 29, 72, 0.24)" : "rgba(148, 163, 184, 0.18)")};
  background: ${(props) =>
    props.$complete
      ? "linear-gradient(180deg, rgba(236, 253, 245, 0.98), rgba(255, 255, 255, 0.98))"
      : props.$active
        ? "linear-gradient(180deg, rgba(255, 241, 242, 0.98), rgba(255, 255, 255, 0.98))"
        : "rgba(255, 255, 255, 0.96)"};
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.04);
`;

const StepperNumber = styled.div<{ $active: boolean; $complete: boolean }>`
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-weight: 800;
  background: ${(props) =>
    props.$complete ? "rgba(16, 185, 129, 0.14)" : props.$active ? "rgba(225, 29, 72, 0.14)" : "rgba(148, 163, 184, 0.12)"};
  color: ${(props) => (props.$complete ? "#047857" : props.$active ? "#be123c" : "#475569")};
`;

const StepperTitle = styled.h3`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: 0.98rem;
`;

const StepperDescription = styled.p`
  margin: 0.2rem 0 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.84rem;
  line-height: 1.55;
`;

const StepperConnector = styled.div`
  position: absolute;
  top: 50%;
  right: -0.6rem;
  width: 1.2rem;
  height: 1px;
  background: rgba(148, 163, 184, 0.35);

  @media (max-width: 900px) {
    display: none;
  }
`;

const CompletionBanner = styled.section`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1rem 1.1rem;
  border-radius: 22px;
  background: linear-gradient(135deg, rgba(236, 253, 245, 0.98), rgba(255, 255, 255, 0.98));
  border: 1px solid rgba(16, 185, 129, 0.18);
`;

const CompletionIcon = styled.div`
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #10b981, #34d399);
  color: #fff;
  flex-shrink: 0;
`;

const CompletionTitle = styled.h3`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: 1rem;
`;

const CompletionCopy = styled.p`
  margin: 0.25rem 0 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.88rem;
  line-height: 1.55;
`;

const DocumentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const DocumentCard = styled.section`
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.04);
  padding: 1.1rem;
`;

const DocumentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 0.85rem;
`;

const DocumentIcon = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: rgba(225, 29, 72, 0.1);
  color: #e11d48;
  flex-shrink: 0;
`;

const DocumentTitle = styled.h3`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: 1rem;
`;

const DocumentDescription = styled.p`
  margin: 0.2rem 0 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.88rem;
  line-height: 1.55;
`;

const DocumentMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const DocumentNote = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.84rem;
  line-height: 1.55;
`;

const DocumentStatus = styled.span<{ $tone: "emerald" | "amber" }>`
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  background: ${(props) => (props.$tone === "emerald" ? "rgba(16, 185, 129, 0.12)" : "rgba(245, 158, 11, 0.12)")};
  color: ${(props) => (props.$tone === "emerald" ? "#047857" : "#b45309")};
`;

const UploadShell = styled.div`
  border-radius: 18px;
  border: 1px dashed rgba(148, 163, 184, 0.28);
  background: rgba(248, 250, 252, 0.85);
  padding: 0.75rem;
`;

const UploadedState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const PreviewFrame = styled.div`
  border-radius: 18px;
  overflow: hidden;
  background: rgba(248, 250, 252, 0.9);
  border: 1px solid rgba(226, 232, 240, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 9.5rem;
`;

const FilePreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  color: ${(props) => props.theme.colors.textSecondary};

  p {
    margin: 0;
    font-size: 0.84rem;
  }
`;

const UploadedCopy = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.88rem;
  line-height: 1.55;
`;

const CheckMark = styled.div`
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #10b981, #34d399);
  color: #fff;
  flex-shrink: 0;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;

  button {
    white-space: nowrap;
  }
`;

const ReviewCard = styled.section`
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(255, 255, 255, 0.98);
  padding: 1.1rem;
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.04);
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ReviewTitle = styled.h3`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: 1.05rem;
`;

const ReviewCopy = styled.p`
  margin: 0.35rem 0 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.88rem;
  line-height: 1.55;
`;

const ReviewPill = styled.span<{ $tone: "emerald" | "amber" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.8rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  background: ${(props) => (props.$tone === "emerald" ? "rgba(16, 185, 129, 0.12)" : "rgba(245, 158, 11, 0.12)")};
  color: ${(props) => (props.$tone === "emerald" ? "#047857" : "#b45309")};
`;

const ReviewActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;

  button {
    white-space: nowrap;
  }
`;

const SettlementCard = styled.section`
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(255, 255, 255, 0.98);
  padding: 1.1rem;
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.04);
`;

const SettlementHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SettlementTitle = styled.h3`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: 1.05rem;
`;

const SettlementDescription = styled.p`
  margin: 0.35rem 0 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.88rem;
  line-height: 1.55;
`;

const TabRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border-radius: 999px;
  border: 1px solid ${(props) => (props.$active ? "rgba(225, 29, 72, 0.3)" : "rgba(148, 163, 184, 0.2)")};
  background: ${(props) => (props.$active ? "rgba(225, 29, 72, 0.08)" : "transparent")};
  color: ${(props) => (props.$active ? "#be123c" : props.theme.colors.textSecondary)};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${(props) => (props.$active ? "rgba(225, 29, 72, 0.12)" : "rgba(148, 163, 184, 0.08)")};
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div<{ $full?: boolean }>`
  grid-column: ${(props) => (props.$full ? "1 / -1" : "auto")};
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.84rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(248, 250, 252, 0.8);
  font-size: 0.88rem;
  color: ${(props) => props.theme.colors.text};
  outline: none;
  transition: border-color 0.15s;

  &:focus {
    border-color: rgba(225, 29, 72, 0.4);
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.textSecondary};
    opacity: 0.6;
  }
`;

const FieldSelect = styled.select`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(248, 250, 252, 0.8);
  font-size: 0.88rem;
  color: ${(props) => props.theme.colors.text};
  outline: none;
  transition: border-color 0.15s;
  cursor: pointer;

  &:focus {
    border-color: rgba(225, 29, 72, 0.4);
  }
`;

const SettlementActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

export default KYCVerification;
